import { useNostr } from '@nostrify/react';
import { useQuery } from '@tanstack/react-query';
import { parseStall, type Stall } from '@/lib/marketplace';
import type { NostrEvent } from '@nostrify/nostrify';

export interface BrowseStallEntry {
  event: NostrEvent;
  stall: Stall;
  merchantPubkey: string;
}

/**
 * Fetch public kind:30017 stall events from the network.
 * Uses a longer timeout and deduplicates by pubkey+d-tag so only the
 * latest version of each stall is kept (addressable event semantics).
 */
export function useBrowseStalls() {
  const { nostr } = useNostr();

  return useQuery<BrowseStallEntry[]>({
    queryKey: ['browse-stalls'],
    queryFn: async () => {
      const events = await nostr.query(
        [{ kinds: [30017], limit: 100 }],
        { signal: AbortSignal.timeout(20000) }
      );

      // Deduplicate: for addressable events keep only the newest per pubkey+d
      const latest = new Map<string, NostrEvent>();
      for (const event of events) {
        const d = event.tags.find(t => t[0] === 'd')?.[1] ?? '';
        const key = `${event.pubkey}:${d}`;
        const existing = latest.get(key);
        if (!existing || event.created_at > existing.created_at) {
          latest.set(key, event);
        }
      }

      const result: BrowseStallEntry[] = [];
      for (const event of latest.values()) {
        const stall = parseStall(event.content);
        if (stall) {
          result.push({ event, stall, merchantPubkey: event.pubkey });
        }
      }

      // Sort newest first
      result.sort((a, b) => b.event.created_at - a.event.created_at);
      return result;
    },
    staleTime: 60000,
    retry: 2,
  });
}
